import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Tenants = ({ primary }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantDevices, setTenantDevices] = useState([]);
  const [loadingTenant, setLoadingTenant] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ name: '', code: '', type: 'distributor', branding: { primary_color: '#1B6B3A', app_name: 'My Broadband', logo_url: '', desk_url: '' }, contact: { email: '', phone: '', address: '' } });

  const inp = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box' };
  const lbl = { fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' };
  const btn = { padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' };

  useEffect(() => { loadTenants(); }, []);

  const loadTenants = async () => {
    try {
      const res = await axios.get('/api/tenants');
      setTenants(res.data.tenants || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await axios.post('/api/tenants', form);
      setMsg('Tenant created successfully!');
      setShowAdd(false);
      loadTenants();
    } catch (e) { setMsg(e.response?.data?.message || 'Failed'); }
  };

  const handleEdit = (t) => {
    setForm({
      name: t.name || '',
      code: t.code || '',
      type: t.type || 'distributor',
      branding: {
        primary_color: t.branding?.primary_color || '#1B6B3A',
        app_name: t.branding?.app_name || '',
        logo_url: t.branding?.logo_url || '',
        desk_url: t.branding?.desk_url || ''
      },
      contact: {
        email: t.contact?.email || '',
        phone: t.contact?.phone || '',
        address: t.contact?.address || ''
      }
    });
    setEditingTenant(t);
  };

  const handleUpdate = async () => {
    try {
      await axios.put('/api/tenants/' + editingTenant._id, {
        name: form.name,
        type: form.type,
        branding: form.branding,
        contact: form.contact
      });
      setEditingTenant(null);
      setMsg('Tenant updated successfully!');
      loadTenants();
    } catch (e) { alert('Update failed: ' + (e.response?.data?.message || e.message)); }
  };

  const uploadLogo = async (file, callback) => {
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const res = await axios.post('/api/tenants/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) callback(res.data.url);
    } catch(e) { alert('Upload failed: ' + e.message); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm('Delete tenant ' + t.name + '? This cannot be undone!')) return;
    try {
      await axios.delete('/api/tenants/' + t._id);
      setMsg('Tenant deleted successfully!');
      loadTenants();
    } catch(e) { setMsg(e.response?.data?.message || 'Failed to delete'); }
  };

  const viewTenant = async (tenant) => {
    setSelectedTenant(tenant);
    setLoadingTenant(true);
    try {
      const [usersRes, devicesRes] = await Promise.all([
        axios.get('/api/users?tenant_id=' + tenant._id),
        axios.get('/api/devices?tenant_id=' + tenant._id)
      ]);
      setTenantUsers(usersRes.data.users || []);
      setTenantDevices(devicesRes.data.devices || []);
    } catch(e) { console.error(e); }
    finally { setLoadingTenant(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#1a2332' }}>Tenant Management</h2>
        <button onClick={() => setShowAdd(!showAdd)} style={{ ...btn, background: primary, color: 'white', padding: '10px 20px' }}>
          {showAdd ? '✕ Cancel' : '+ Add Tenant'}
        </button>
      </div>

      {msg && <div style={{ background: msg.includes('success') ? '#e8f5e9' : '#ffebee', color: msg.includes('success') ? '#2e7d32' : '#c62828', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{msg}</div>}

      {showAdd && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 20px', color: primary }}>Create New Tenant</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <h4 style={{ margin: '0 0 12px', color: '#333' }}>Basic Info</h4>
              <label style={lbl}>Company Name *</label>
              <input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. PDIC Broadband" />
              <label style={lbl}>Tenant Code * (unique)</label>
              <input style={inp} value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. PDIC" />
              <label style={lbl}>Type</label>
              <select style={inp} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="isp">ISP</option>
                <option value="distributor">Distributor</option>
                <option value="lco">LCO</option>
              </select>
              <label style={lbl}>Contact Email</label>
              <input style={inp} value={form.contact.email} onChange={e => setForm({ ...form, contact: { ...form.contact, email: e.target.value } })} placeholder="admin@company.com" />
              <label style={lbl}>Contact Phone</label>
              <input style={inp} value={form.contact.phone} onChange={e => setForm({ ...form, contact: { ...form.contact, phone: e.target.value } })} placeholder="Phone number" />
            </div>
            <div>
              <h4 style={{ margin: '0 0 12px', color: '#333' }}>Branding</h4>
              <label style={lbl}>App/Portal Name</label>
              <input style={inp} value={form.branding.app_name} onChange={e => setForm({ ...form, branding: { ...form.branding, app_name: e.target.value } })} placeholder="e.g. PDIC Connect" />
              <label style={lbl}>Primary Color</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input type="color" value={form.branding.primary_color} onChange={e => setForm({ ...form, branding: { ...form.branding, primary_color: e.target.value } })} style={{ width: '50px', height: '40px', borderRadius: '6px', border: '1px solid #ddd', cursor: 'pointer' }} />
                <input style={{ ...inp, marginBottom: 0, flex: 1 }} value={form.branding.primary_color} onChange={e => setForm({ ...form, branding: { ...form.branding, primary_color: e.target.value } })} placeholder="#1B6B3A" />
              </div>
              <label style={lbl}>Logo</label>
              <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadLogo(e.target.files[0], url => setForm({...form, branding: {...form.branding, logo_url: 'https://acs.fibralink.co.in' + url}}))} style={{ marginBottom: '8px', fontSize: '13px' }} />
              <input style={inp} value={form.branding.logo_url} onChange={e => setForm({ ...form, branding: { ...form.branding, logo_url: e.target.value } })} placeholder="Or paste URL https://..." />
              {form.branding.logo_url && <img src={form.branding.logo_url} alt="logo preview" style={{ width: '100px', height: '60px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '8px', padding: '4px' }} />}
              <label style={lbl}>Support Desk URL</label>
              <input style={inp} value={form.branding.desk_url || ''} onChange={e => setForm({ ...form, branding: { ...form.branding, desk_url: e.target.value } })} placeholder="https://desk.pdic.support" />
              <div style={{ marginTop: '16px', padding: '16px', background: form.branding.primary_color, borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{form.branding.app_name || 'App Name'}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>{form.name || 'Company Name'}</div>
              </div>
            </div>
          </div>
          <button onClick={handleCreate} style={{ ...btn, background: primary, color: 'white', padding: '12px 28px', marginTop: '8px' }}>Create Tenant</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
        {loading ? <p>Loading...</p> : tenants.map(t => (
          <div key={t._id} style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ background: t.branding?.primary_color || primary, padding: '20px', color: 'white' }}>
              {t.branding?.logo_url && <img src={t.branding?.logo_url} alt="logo" style={{ height: '40px', objectFit: 'contain', marginBottom: '8px' }} />}
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{t.name}</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>{t.branding?.app_name}</div>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>Code:</span>
                <span style={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{t.code}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>Type:</span>
                <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: '20px', fontSize: '12px' }}>{t.type.toUpperCase()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>Status:</span>
                <span style={{ background: t.is_active ? '#e8f5e9' : '#ffebee', color: t.is_active ? '#2e7d32' : '#c62828', padding: '2px 8px', borderRadius: '20px', fontSize: '12px' }}>
                  {t.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0', fontSize: '12px', color: '#666' }}>
                <div>Portal: myconnection.fibralink.co.in</div>
                <div>Code: {t.code}</div>
              </div>
              <button onClick={() => viewTenant(t)} style={{ width: '100%', marginTop: '12px', padding: '10px', background: t.branding?.primary_color || primary, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                👁️ View Network Hierarchy
              </button>
              <button onClick={() => handleEdit(t)} style={{ width: '100%', marginTop: '8px', padding: '10px', background: 'white', color: t.branding?.primary_color || primary, border: '2px solid ' + (t.branding?.primary_color || primary), borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                ✏️ Edit Settings
              </button>
              <button onClick={() => handleDelete(t)} style={{ width: '100%', marginTop: '8px', padding: '10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                🗑️ Delete Tenant
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal — inline, has access to all state */}
      {editingTenant && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#333' }}>Edit — {editingTenant.name}</h2>
              <button onClick={() => setEditingTenant(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Organization Name</label>
              <input style={inp} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <h4 style={{ color: '#333', margin: '16px 0 8px' }}>Contact Details</h4>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Support Phone</label>
              <input style={inp} value={form.contact.phone} onChange={e => setForm({...form, contact: {...form.contact, phone: e.target.value}})} placeholder="e.g. 9876543210" />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Support Email</label>
              <input style={inp} value={form.contact.email} onChange={e => setForm({...form, contact: {...form.contact, email: e.target.value}})} placeholder="support@company.com" />
            </div>
            <h4 style={{ color: '#333', margin: '16px 0 8px' }}>Branding</h4>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>App Name</label>
              <input style={inp} value={form.branding.app_name} onChange={e => setForm({...form, branding: {...form.branding, app_name: e.target.value}})} placeholder="e.g. PDIC Connect" />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Primary Color</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input type="color" value={form.branding.primary_color} onChange={e => setForm({...form, branding: {...form.branding, primary_color: e.target.value}})} style={{ width: '50px', height: '40px', borderRadius: '6px', border: '1px solid #ddd', cursor: 'pointer' }} />
                <input style={{...inp, marginBottom: 0, flex: 1}} value={form.branding.primary_color} onChange={e => setForm({...form, branding: {...form.branding, primary_color: e.target.value}})} />
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Logo</label>
              <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadLogo(e.target.files[0], url => setForm({...form, branding: {...form.branding, logo_url: 'https://acs.fibralink.co.in' + url}}))} style={{ marginBottom: '8px', fontSize: '13px' }} />
              <input style={inp} value={form.branding.logo_url} onChange={e => setForm({...form, branding: {...form.branding, logo_url: e.target.value}})} placeholder="Or paste URL https://..." />
              {form.branding.logo_url && <img src={form.branding.logo_url} alt="logo preview" style={{ width: '100px', height: '60px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '8px', padding: '4px', marginTop: '8px' }} />}
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Support Desk URL</label>
              <input style={inp} value={form.branding.desk_url || ''} onChange={e => setForm({...form, branding: {...form.branding, desk_url: e.target.value}})} placeholder="https://desk.pdic.support" />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={handleUpdate} style={{ flex: 1, padding: '12px', background: primary, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>💾 Save Changes</button>
              <button onClick={() => setEditingTenant(null)} style={{ flex: 1, padding: '12px', background: 'white', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Drill-Down Panel */}
      {selectedTenant && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
          <div style={{ width: '600px', height: '100vh', background: 'white', boxShadow: '-4px 0 40px rgba(0,0,0,0.2)', overflowY: 'auto' }}>
            <div style={{ background: selectedTenant.branding?.primary_color || primary, padding: '24px', color: 'white', position: 'sticky', top: 0, zIndex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: '800', fontSize: '20px' }}>{selectedTenant.name}</div>
                  <div style={{ fontSize: '13px', opacity: 0.8 }}>{selectedTenant.branding?.app_name} · {selectedTenant.code}</div>
                </div>
                <button onClick={() => setSelectedTenant(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '10px', width: '36px', height: '36px', cursor: 'pointer', color: 'white', fontSize: '18px' }}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: '20px', marginTop: '16px' }}>
                {[
                  { label: 'Subs', value: tenantUsers.filter(u=>u.role==='sub_dist_admin').length },
                  { label: 'LCOs', value: tenantUsers.filter(u=>u.role==='lco_admin').length },
                  { label: 'Devices', value: tenantDevices.length },
                  { label: 'Online', value: tenantDevices.filter(d=>d.status==='online').length }
                ].map(s => (
                  <div key={s.label} style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '800', fontSize: '22px' }}>{s.value}</div>
                    <div style={{ fontSize: '11px', opacity: 0.7, textTransform: 'uppercase' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              {loadingTenant ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading...</div>
              ) : (
                <>
                  {tenantUsers.filter(u=>u.role==='sub_dist_admin').length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏘️</div>
                      <div>No Sub-Distributions found</div>
                    </div>
                  ) : (
                    tenantUsers.filter(u=>u.role==='sub_dist_admin').map(sub => {
                      const subLcos = tenantUsers.filter(u => u.role==='lco_admin' && u.sub_dist_id && u.sub_dist_id.toString()===sub._id.toString());
                      const subDevices = tenantDevices.filter(d => d.sub_dist_id && d.sub_dist_id.toString()===sub._id.toString());
                      const isExpanded = expandedSub === sub._id;
                      return (
                        <div key={sub._id} style={{ background: '#f8fafc', borderRadius: '14px', marginBottom: '12px', overflow: 'hidden', border: '1px solid #f1f5f9' }}>
                          <div onClick={() => setExpandedSub(isExpanded ? null : sub._id)} style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🏘️</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>{sub.name}</div>
                              <div style={{ fontSize: '12px', color: '#94a3b8' }}>{sub.area || 'No area'} · {sub.lco_id || sub.email}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '700', color: '#0f172a' }}>{subLcos.length}</div>
                                <div style={{ fontSize: '10px', color: '#94a3b8' }}>LCOs</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '700', color: '#0f172a' }}>{subDevices.length}</div>
                                <div style={{ fontSize: '10px', color: '#94a3b8' }}>Devices</div>
                              </div>
                              <span style={{ color: '#94a3b8' }}>{isExpanded ? '▲' : '▼'}</span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div style={{ padding: '0 16px 16px' }}>
                              {subLcos.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', fontSize: '13px' }}>No LCOs under this Sub</div>
                              ) : (
                                subLcos.map(lco => {
                                  const lcoDevices = tenantDevices.filter(d => d.lco_id && d.lco_id.toString()===lco._id.toString());
                                  const lcoOnline = lcoDevices.filter(d=>d.status==='online').length;
                                  return (
                                    <div key={lco._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'white', borderRadius: '10px', marginBottom: '8px', border: '1px solid #f1f5f9' }}>
                                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#d97706', marginLeft: '8px', flexShrink: 0 }} />
                                      <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>👤</div>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{lco.name}</div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{lco.lco_id} · {lco.area || 'No area'}</div>
                                      </div>
                                      <div style={{ display: 'flex', gap: '12px' }}>
                                        <div style={{ textAlign: 'center' }}>
                                          <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>{lcoDevices.length}</div>
                                          <div style={{ fontSize: '10px', color: '#94a3b8' }}>Devices</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                          <div style={{ fontWeight: '700', fontSize: '14px', color: '#16a34a' }}>{lcoOnline}</div>
                                          <div style={{ fontSize: '10px', color: '#94a3b8' }}>Online</div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants;
